import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Sparkles, ChevronDown, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const primaryLinks = [
  { to: "/chi-sono", label: "Chi sono" },
  { to: "/aree-di-competenza", label: "Aree" },
  { to: "/consulenze", label: "Consulenze" },
  { to: "/supervisioni", label: "Supervisioni" },
];

const divulgazioneLinks = [
  { to: "/webinar", label: "Webinar" },
  { to: "/blog", label: "Blog" },
  { to: "/pubblicazioni", label: "Pubblicazioni" },
  { to: "/eventi", label: "Eventi" },
  { to: "/media", label: "Media" },
  { to: "/rete", label: "Rete GSRD" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth() || {};
  const { theme, toggle } = useTheme() || {};

  return (
    <header className="sticky top-0 z-50 border-b border-viola/10 bg-cream/80 backdrop-blur-xl dark:bg-viola/70 dark:border-cream/10" data-testid="site-navbar">
      <div className="container-editorial flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="brand-link" aria-label="Dania (Dan) Piras, vai alla home">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-viola text-cream transition-colors group-hover:bg-fucsia dark:bg-giallo dark:text-viola">
            <Sparkles size={18} />
          </span>
          <span className="font-display text-lg lg:text-xl font-semibold tracking-tight dark:text-cream">Dania (Dan) Piras</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Menu principale">
          {primaryLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-viola text-cream dark:bg-giallo dark:text-viola"
                    : "text-viola hover:bg-lilla dark:text-cream dark:hover:bg-cream/10"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="rounded-full px-3 py-2 text-sm font-medium text-viola hover:bg-lilla dark:text-cream dark:hover:bg-cream/10 inline-flex items-center gap-1"
                data-testid="nav-divulgazione"
              >
                Divulgazione <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              {divulgazioneLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link to={l.to} data-testid={`dropdown-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink
            to="/contatti"
            data-testid="nav-contatti"
            className={({ isActive }) =>
              `rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-viola text-cream dark:bg-giallo dark:text-viola"
                  : "text-viola hover:bg-lilla dark:text-cream dark:hover:bg-cream/10"
              }`
            }
          >
            Contatti
          </NavLink>
          {user?.is_admin && (
            <Link to="/admin" data-testid="nav-admin" className="ml-1 rounded-full bg-giallo px-3 py-2 text-sm font-semibold text-viola hover:bg-arancio">
              Admin
            </Link>
          )}
          <button
            onClick={toggle}
            className="ml-2 grid h-9 w-9 place-items-center rounded-full border border-viola/20 text-viola hover:bg-viola hover:text-cream transition-colors dark:border-cream/25 dark:text-cream dark:hover:bg-cream dark:hover:text-viola"
            aria-label={theme === "dark" ? "Passa alla modalità chiara" : "Passa alla modalità scura"}
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={toggle}
            className="grid h-10 w-10 place-items-center rounded-full border border-viola/20 text-viola dark:border-cream/25 dark:text-cream"
            aria-label={theme === "dark" ? "Passa alla modalità chiara" : "Passa alla modalità scura"}
            data-testid="theme-toggle-mobile"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            className="grid h-10 w-10 place-items-center rounded-full border border-viola/20 text-viola dark:border-cream/25 dark:text-cream"
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Chiudi il menu" : "Apri il menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div id="mobile-menu" className="lg:hidden border-t border-viola/10 bg-cream dark:bg-viola dark:border-cream/10" data-testid="mobile-menu">
          <nav className="container-editorial py-4 flex flex-col gap-1" aria-label="Menu principale">
            {[...primaryLinks, ...divulgazioneLinks, { to: "/contatti", label: "Contatti" }].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.to.slice(1)}`}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-3 text-base font-medium ${isActive ? "bg-viola text-cream dark:bg-giallo dark:text-viola" : "hover:bg-lilla text-viola dark:text-cream dark:hover:bg-cream/10"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user?.is_admin && (
              <Link to="/admin" onClick={() => setOpen(false)} className="rounded-lg bg-giallo px-4 py-3 font-semibold text-viola" data-testid="mobile-nav-admin">
                Admin
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
